import React, { useEffect, useState } from 'react';
import { useNCZIContext } from '../../Services/NCZIContext';
import NCZICommandResult from '../NCZICommandResult/NCZICommandResult';
import useNCZICommandProcess from '../../Hooks/useNCZICommandProcess';
import { VerifyPatientInsurance } from '../../Commands/VerifyPatientInsurance';
import Text from '../../../../Components/Base/Text/Text';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import PatientNonPayerModal from './PatientNonPayerModal';
import { usePatientTreatmentContext } from '../../../Patient/Components/PatientTreatment/ContextProvider';

interface Props {
	patientId: string;
	onClose: () => void;
}

const PatientInsuranceSetupVerify: React.FC<Props> = props => {
	const {
		patientBirthNumber,
		patientInsurance,
		setPatientInsurance,
		setPatientInsuranceNonPayer,
		setDeferredZsEligibility,
		patientMeddiInsurance,
	} = useNCZIContext();
	const [finished, setFinished] = useState<boolean>(false);
	const { patientId } = usePatientTreatmentContext();
	const [nonPayerModalOpened, setNonPayerModalOpened] =
		useState<boolean>(false);
	const [tmpInsurance, setTmpInsurance] = useState<string>('');
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { processCommand, isProcessing, processingCommandId } =
		useNCZICommandProcess({
			hideSuccess: true,
			onFinish: (
				id,
				result: {
					isInsured: boolean;
					isInsuredInEU: boolean;
					nonPayer: boolean;
					deferredZsEligibility: boolean | undefined;
					insurance: 'union' | 'dovera' | 'vszp';
				},
			) => {
				if (result.isInsured || result.isInsuredInEU) {
					setActiveInsurance(
						result.insurance,
						result.nonPayer,
						result.deferredZsEligibility,
					);
				}

				setFinished(true);
			},
			onError: (id, message: string) => {
				setFinished(true);
			},
		});

	const setActiveInsurance = async (
		insuranceName: 'union' | 'dovera' | 'vszp',
		nonPayer: boolean,
		deferredZsEligibility: boolean | undefined,
	) => {
		setTmpInsurance(insuranceName);
		setPatientInsuranceNonPayer(nonPayer);
		if (typeof deferredZsEligibility !== 'undefined') {
			setDeferredZsEligibility(deferredZsEligibility);
		}
		showNotificationSuccess({
			content: t('nczi.verifyPatientInsurance.foundActive'),
		});

		if (nonPayer) {
			setNonPayerModalOpened(true);
		} else {
			setPatientInsurance(insuranceName);
			props.onClose();
		}
	};

	const closeNonPayerModal = () => {
		setPatientInsurance(tmpInsurance);
		props.onClose();
	};

	// check only one insurance
	useEffect(() => {
		if (patientMeddiInsurance) {
			checkInsurance(patientMeddiInsurance);
		} else if (
			patientMeddiInsurance === null ||
			typeof patientMeddiInsurance === 'undefined'
		) {
			setFinished(true);
		}
	}, [patientMeddiInsurance]);

	const checkInsurance = async (insuranceName: string) => {
		const verifyCommand = VerifyPatientInsurance({
			insurance: insuranceName,
			birthNumber: patientBirthNumber!,
			patientId,
		});

		await processCommand(verifyCommand);
	};

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<>
			{finished && !patientInsurance && (
				<Text
					tx={'nczi.verifyPatientInsurance.cannotFindInsurance'}
					firstUpper
				/>
			)}
			{!finished && (
				<Text tx={'nczi.verifyPatientInsurance.searching'} firstUpper />
			)}
			<PatientNonPayerModal
				opened={nonPayerModalOpened}
				onClose={closeNonPayerModal}
			/>
		</>
	);
};

export default PatientInsuranceSetupVerify;
